import { STABLECOIN_BASE_TOKENS } from '../constants/currency'

/**
 * Currency Decimals Registry
 *
 * Module-level Map storing currency code → decimals mapping.
 * Populated at app initialization from the DB via GraphQL,
 * with hardcoded fallback for safety before data loads.
 */
const currencyDecimalsMap = new Map<string, number>()

/**
 * Load currency decimals from DB data (called once at app init)
 */
export function setCurrencyDecimals(
  currencies: { code: string; decimals: number }[]
): void {
  currencies.forEach((c) => currencyDecimalsMap.set(c.code, c.decimals))
}

/**
 * Get decimal places for a currency code.
 * Returns DB value if available, otherwise falls back to hardcoded defaults.
 */
export function getDecimalsForCurrency(currencyCode: string): number {
  const dbValue = currencyDecimalsMap.get(currencyCode)
  if (dbValue !== undefined) return dbValue

  return fallbackDecimals(currencyCode)
}

/**
 * Hardcoded fallback for when DB data is not yet loaded.
 * Mirrors the original getCurrencyDecimals logic.
 */
function fallbackDecimals(currency: string): number {
  // Extract base token for network-specific codes (e.g. TTRON_USDT → USDT)
  const baseCurrency = extractBaseToken(currency)

  if (STABLECOIN_BASE_TOKENS.includes(baseCurrency)) {
    return 6
  }

  const zeroDecimalCurrencies = [
    'KRW',
    'JPY',
    'VND',
    'IDR',
    'CLP',
    'PYG',
    'UGX',
    'XAF',
    'XOF',
    'XPF',
  ]

  const threeDecimalCurrencies = ['BHD', 'JOD', 'KWD', 'OMR', 'TND']

  if (zeroDecimalCurrencies.includes(baseCurrency.toUpperCase())) {
    return 0
  }

  if (threeDecimalCurrencies.includes(baseCurrency.toUpperCase())) {
    return 3
  }

  return 2
}

function extractBaseToken(currency: string): string {
  if (currency.includes('_')) {
    const parts = currency.split('_')
    const lastPart = parts[parts.length - 1]
    if (STABLECOIN_BASE_TOKENS.includes(lastPart)) {
      return lastPart
    }
  }
  return currency
}
